<template>
  <dialog-modal :is-open="isOpen" :title="modal_title" :show-close-button="true" :processing="saving" @close="closeModal">
    <div class="w-full flex flex-col gap-3">
      <div v-if="error_message" class="text-danger text-xs leading-4">{{ error_message }}</div>

      <!-- List mode: the caller's own User groups. Enterprise groups are deliberately absent —
           they are admin-managed (descoped for now) and not editable here either way. -->
      <template v-if="mode === 'list'">
        <div v-if="my_groups.length === 0" class="text-text-subtle text-sm leading-5">
          {{ $t('messaging.groups.noGroups') }}
        </div>
        <div v-for="group in my_groups" :key="group.id"
             class="self-stretch px-3 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-border-separator flex justify-between items-center gap-2">
          <div class="flex-1 min-w-0">
            <div class="text-text-default text-sm font-semibold leading-4 truncate">{{ group.name }}</div>
            <div class="text-text-subtle text-xs leading-4 truncate">{{ memberSummary(group) }}</div>
          </div>
          <template v-if="confirm_delete_id === group.id">
            <span class="text-text-default text-xs leading-4">{{ $t('messaging.groups.deleteConfirm') }}</span>
            <span class="text-danger text-xs font-semibold leading-4 cursor-pointer hover:underline" :data-test="`confirm-delete-${group.id}`" @click="confirmDelete(group)">{{ $t('messaging.groups.delete') }}</span>
            <span class="text-text-link text-xs leading-4 cursor-pointer hover:underline" @click="confirm_delete_id = null">{{ $t('common.cancel') }}</span>
          </template>
          <template v-else>
            <i class="ri-pencil-line ri-1x text-text-subtle cursor-pointer hover:text-text-link" :title="$t('messaging.groups.editGroup')" :data-test="`edit-${group.id}`" @click="openEdit(group)" />
            <i class="ri-delete-bin-line ri-1x text-text-subtle cursor-pointer hover:text-danger" :title="$t('messaging.groups.delete')" :data-test="`delete-${group.id}`" @click="confirm_delete_id = group.id" />
          </template>
        </div>
        <div class="self-stretch h-9 px-4 py-2 bg-btn-primary rounded-md flex justify-center items-center cursor-pointer hover:opacity-90 transition-opacity" data-test="new-group" @click="openCreate()">
          <span class="text-white text-sm leading-4">{{ $t('messaging.groups.newGroup') }}</span>
        </div>
      </template>

      <!-- Edit/create mode -->
      <template v-else>
        <div class="flex flex-col gap-1">
          <label class="text-text-default text-xs font-semibold leading-4">{{ $t('messaging.groups.groupName') }}</label>
          <input v-model="form_name" type="text" maxlength="100" :placeholder="$t('messaging.groups.groupNamePlaceholder')"
                 data-test="group-name"
                 class="self-stretch px-3 py-2 bg-surface-1 rounded-md outline outline-1 outline-offset-[-1px] outline-border-default text-text-default text-sm leading-4 focus:outline-brand-outline" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-text-default text-xs font-semibold leading-4">{{ $t('messaging.groups.selectDealerships') }}</label>
          <div class="max-h-48 overflow-y-auto flex flex-col gap-0.5">
            <label v-for="dealer in sorted_dealers" :key="dealer.cifNumber"
                   class="self-stretch px-3 py-1.5 rounded-md hover:bg-surface-3 transition-colors cursor-pointer flex items-center gap-2">
              <input type="checkbox" :value="dealer.cifNumber" v-model="form_cifs" :data-test="`cif-${dealer.cifNumber}`" />
              <span class="text-text-default text-sm leading-4">{{ dealer.name || $t('common.noName') }} ({{ dealer.cifNumber }})</span>
            </label>
          </div>
        </div>
        <div class="self-stretch flex justify-end items-center gap-2">
          <div class="h-9 px-4 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-border-default flex justify-center items-center cursor-pointer hover:bg-surface-3 transition-colors" data-test="cancel-edit" @click="backToList()">
            <span class="text-text-default text-sm leading-4">{{ $t('common.cancel') }}</span>
          </div>
          <div class="h-9 px-4 py-2 rounded-md flex justify-center items-center gap-2 transition-opacity"
               :class="can_save && !saving ? 'bg-btn-primary cursor-pointer hover:opacity-90' : 'bg-btn-primary opacity-50 cursor-not-allowed'"
               data-test="save-group" @click="saveGroup()">
            <div v-if="saving" class="fusion-loader loader-sm" />
            <span class="text-white text-sm leading-4">{{ $t('messaging.groups.save') }}</span>
          </div>
        </div>
      </template>
    </div>
  </dialog-modal>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import DialogModal from '@components/UI/DialogModal.vue';

/**
 * Manage-my-groups modal (dealer-groups plan — User groups only; Enterprise groups are
 * admin-managed and descoped). Opened from the dealership-filter picker in MessengerInput.
 * All rules are enforced server-side by DealerGroupService via /api/dealergroups; this UI
 * only offers the caller's own dealers as members (mirroring the server's user-group rule).
 */
export default {
  name: 'DealerGroupManager',
  components: {
    'dialog-modal': DialogModal,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close', 'groupDeleted'],
  data() {
    return {
      mode: 'list', // 'list' | 'edit'
      editing_group_id: null, // null in edit mode = creating
      form_name: '',
      form_cifs: [],
      saving: false,
      error_message: null,
      confirm_delete_id: null,
    };
  },
  computed: {
    ...mapStores(useMainStore),
    dealer_groups() {
      return this.mainStore.dealer_groups || [];
    },
    user_access_info() {
      return this.mainStore.user_access_info;
    },
    my_groups() {
      return this.dealer_groups
        .filter(g => g.scope === 'User')
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    sorted_dealers() {
      return [...(this.user_access_info?.dealers || [])]
        .sort((a, b) => (a.name || a.cifNumber || '').localeCompare(b.name || b.cifNumber || ''));
    },
    modal_title() {
      if (this.mode === 'edit')
        return this.editing_group_id ? this.$t('messaging.groups.editGroup') : this.$t('messaging.groups.newGroup');
      return this.$t('messaging.groups.title');
    },
    can_save() {
      return this.form_name.trim().length > 0 && this.form_cifs.length > 0;
    },
  },
  methods: {
    closeModal() {
      if (this.saving) return;
      this.backToList();
      this.$emit('close');
    },
    openCreate() {
      this.error_message = null;
      this.confirm_delete_id = null;
      this.editing_group_id = null;
      this.form_name = '';
      this.form_cifs = [];
      this.mode = 'edit';
    },
    openEdit(group) {
      this.error_message = null;
      this.confirm_delete_id = null;
      this.editing_group_id = group.id;
      this.form_name = group.name || '';
      this.form_cifs = [...(group.memberCifs || [])];
      this.mode = 'edit';
    },
    backToList() {
      this.mode = 'list';
      this.editing_group_id = null;
      this.error_message = null;
      this.confirm_delete_id = null;
    },
    memberSummary(group) {
      // Member CIFs resolved to dealer names where known; css-truncated in the row.
      const byCif = new Map(this.sorted_dealers.map(d => [(d.cifNumber || '').toLowerCase(), d.name || d.cifNumber]));
      return (group.memberCifs || [])
        .map(cif => byCif.get((cif || '').toLowerCase()) || cif)
        .join(', ');
    },
    saveGroup() {
      if (!this.can_save || this.saving) return;
      const self = this;
      this.saving = true;
      this.error_message = null;
      const payload = { name: this.form_name.trim(), scope: 'User', memberCifs: [...this.form_cifs] };
      const action = this.editing_group_id
        ? this.mainStore.updateDealerGroup(this.editing_group_id, payload)
        : this.mainStore.createDealerGroup(payload);
      action.then(() => {
        self.saving = false;
        self.backToList();
      }, error => {
        self.saving = false;
        self.error_message = typeof error === 'string' ? error : String(error);
      });
    },
    confirmDelete(group) {
      if (this.saving) return;
      const self = this;
      this.saving = true;
      this.error_message = null;
      this.mainStore.deleteDealerGroup(group.id).then(() => {
        self.saving = false;
        self.confirm_delete_id = null;
        // Let the picker clear its selection if the deleted group was active.
        self.$emit('groupDeleted', group.id);
      }, error => {
        self.saving = false;
        self.confirm_delete_id = null;
        self.error_message = typeof error === 'string' ? error : String(error);
      });
    },
  },
};
</script>
